import { BehaviorSubject, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { SubsectionTreeService } from './subsection-tree.service';
import {
  BaseTreeNode,
  CustomNode,
  isDashboardNode,
  isKpfRootNode,
  isReleaseRootNode,
  isSectionNode,
  isShareLinkRootNode,
  isSubsectionRootNode,
  isSubsectionTopicNode,
  KpfFolderNode,
  ProjectNode,
  ProjectRootNode,
  ProjectSectionNode,
  SectionNode,
  SubsectionNode,
  SubsectionRootNode,
  TreeNodeType,
  TreeState,
} from '../models/tree-node';
import { Project } from '../../shared/interfaces';
import { KpfTreeService } from './kpf-tree.service';
import { HelperService } from '../../shared/services/helper.service';
import { ShareLinkTreeService } from './share-link-tree.service';
import { EmitterService } from '../../shared/services/emitter.service';
import { map, switchMap } from 'rxjs/operators';
import { BaseTreeService } from './base-tree.service';
import { SharePreviewTreeService } from './share-preview-tree.service';
import { ReleaseTreeService } from './release-tree-service';
import { GeneralUtilsService } from '../../shared/services/general-utils.service';
import { EMPTY_UUID } from '../../shared/constants';

import { ShareLinkTreeState } from '../models';

export enum TreeManagerTreeType {
  Kpf = 'kpf',
  ShareLink = 'share-link',
  Subsection = 'subsection',
  ShareLinkPreview = 'share-link-preview',
  Release = 'release',
}

@Injectable({
  providedIn: 'root',
})
export class TreeManagerService {
  private currentTreeType: TreeManagerTreeType = TreeManagerTreeType.Subsection;

  private readonly subsectionTreeService = inject(SubsectionTreeService);
  private readonly kpfTreeService = inject(KpfTreeService);
  private readonly shareLinkTreeService = inject(ShareLinkTreeService);
  private readonly sharePreviewTreeService = inject(SharePreviewTreeService);
  private readonly releaseTreeService = inject(ReleaseTreeService);
  private readonly generalUtilsService = inject(GeneralUtilsService);
  private readonly activeTreeType$ = new BehaviorSubject<TreeManagerTreeType>(this.currentTreeType);
  private projectTree: ProjectRootNode | null = null;
  private readonly projectTree$ = new BehaviorSubject<ProjectNode | null>(this.projectTree);
  private activeTreeService: BaseTreeService<BaseTreeNode> = this.subsectionTreeService;
  private readonly activeTreeService$ = new BehaviorSubject<BaseTreeService<BaseTreeNode>>(this.activeTreeService);

  private readonly currentTreeState$ = this.activeTreeType$.pipe(
    switchMap((treeType) => {
      switch (treeType) {
        case TreeManagerTreeType.Kpf:
          return this.kpfTreeService.getState();
        case TreeManagerTreeType.ShareLink:
          return this.shareLinkTreeService.getState();
        case TreeManagerTreeType.Subsection:
          return this.subsectionTreeService.getState();
        case TreeManagerTreeType.ShareLinkPreview:
          return this.sharePreviewTreeService.getState();
        case TreeManagerTreeType.Release:
          return this.releaseTreeService.getState();
        default:
          return this.subsectionTreeService.getState();
      }
    })
  );

  getProjectTree(): Observable<ProjectNode | null> {
    return this.projectTree$.asObservable();
  }

  getCurrentTreeService(): Observable<BaseTreeService<BaseTreeNode>> {
    return this.activeTreeService$.asObservable();
  }

  //if require more functionality, better extract to separate service class
  buildProjectTree(project: Project) {
    if (!project?.id) {
      return;
    }

    const rootNode: ProjectRootNode = {
      id: project.id!,
      name: project.name!,
      type: TreeNodeType.ProjectRoot,
      isExpandable: true,
      isSelected: true,
      isExpanded: true,
      children: [] as ProjectNode[],
      parent: null,
      depth: 0,
      data: project,
    };

    (project.sections || []).forEach((section) => {
      if (!section.projectSubSections?.length) {
        return;
      }

      const sectionNode: ProjectSectionNode = {
        id: section.id,
        name: section.name,
        type: TreeNodeType.ProjectSection,
        isExpandable: true,
        isSelected: false,
        isExpanded: false,
        children: [] as ProjectNode[],
        parent: rootNode,
        depth: rootNode.depth + 1,
        data: section,
      };
      rootNode.children.push(sectionNode);

      (section.projectSubSections || []).forEach((subsection) => {
        const subsectionNode: SubsectionRootNode = {
          id: subsection.id!,
          name: subsection.name!,
          type: TreeNodeType.SubsectionRoot,
          isExpandable: true,
          isSelected: false,
          isExpanded: true,
          children: [],
          projectId: project.id!,
          parent: sectionNode,
          depth: sectionNode.depth + 1,
          data: subsection,
        };
        sectionNode.children.push(subsectionNode);
      });
    });

    const dashboardNode: CustomNode = {
      id: EMPTY_UUID,
      name: 'Dashboard',
      type: TreeNodeType.CustomNode,
      customType: 'dashboard',
      isExpandable: true,
      isSelected: false,
      isExpanded: true,
      children: [],
      parent: rootNode,
      depth: rootNode.depth + 1,
    };
    rootNode.children.push(dashboardNode);

    const kpfRootNode: KpfFolderNode = {
      id: EMPTY_UUID,
      name: 'Key Project Files',
      type: TreeNodeType.KpfRoot,
      isExpandable: true,
      isSelected: false,
      isExpanded: false,
      children: [] as ProjectNode[],
      parent: rootNode,
      depth: rootNode.depth + 1,
    };
    rootNode.children.push(kpfRootNode);

    this.projectTree = rootNode;
    this.projectTree$.next(rootNode);
  }

  buildCustomProjectTree(rootNode: ProjectRootNode) {
    this.projectTree = rootNode;
    this.projectTree$.next(rootNode);
  }

  clearProjectTree() {
    this.projectTree = null;
    this.projectTree$.next(null);
  }

  getActiveTreeType(): Observable<TreeManagerTreeType> {
    return this.activeTreeType$.asObservable();
  }

  getShareLinkTreeRoot(): Observable<BaseTreeNode | null> {
    return this.shareLinkTreeService.getState().pipe(map((state: ShareLinkTreeState) => state.root));
  }

  setTreeType(type: TreeManagerTreeType) {
    if (type == this.currentTreeType) {
      return;
    }

    this.currentTreeType = type;
    this.activeTreeType$.next(type);
    switch (type) {
      case TreeManagerTreeType.Kpf:
        this.activeTreeService = this.kpfTreeService;
        break;
      case TreeManagerTreeType.ShareLink:
        this.activeTreeService = this.shareLinkTreeService;
        break;
      case TreeManagerTreeType.Subsection:
        this.activeTreeService = this.subsectionTreeService;
        break;
      case TreeManagerTreeType.ShareLinkPreview:
        this.activeTreeService = this.sharePreviewTreeService;
        break;
      case TreeManagerTreeType.Release:
        this.activeTreeService = this.releaseTreeService;
        break;
      default:
        this.activeTreeService = this.subsectionTreeService;
        break;
    }
    this.activeTreeService$.next(this.activeTreeService);
  }

  findNodeById(id: string): BaseTreeNode | null {
    switch (this.currentTreeType) {
      case TreeManagerTreeType.Kpf:
        return this.kpfTreeService.findNodeById(id);
      case TreeManagerTreeType.ShareLink:
        return this.shareLinkTreeService.findNodeById(id);
      case TreeManagerTreeType.Subsection:
        return this.subsectionTreeService.findNodeById(id);
      case TreeManagerTreeType.ShareLinkPreview:
        return this.sharePreviewTreeService.findNodeById(id);
      case TreeManagerTreeType.Release:
        return this.releaseTreeService.findNodeById(id);
      default:
        return this.subsectionTreeService.findNodeById(id);
    }
  }

  navigateToNode(node: BaseTreeNode) {
    if (isDashboardNode(node)) {
      return EmitterService.get('SUBSECTION_NAVIGATION:DASHBOARD').emit();
    }

    if (isSectionNode(node)) {
      this.setTreeType(TreeManagerTreeType.Subsection);
      return EmitterService.get('SUBSECTION_NAVIGATION:SELECT_NODE').emit(node as SectionNode);
    }

    if (isSubsectionRootNode(node) || isSubsectionTopicNode(node) || isSectionNode(node)) {
      this.setTreeType(TreeManagerTreeType.Subsection);
      return EmitterService.get('SUBSECTION_NAVIGATION:SELECT_NODE').emit(node as SubsectionRootNode);
    }

    if (isKpfRootNode(node)) {
      EmitterService.get('SUBSECTION_NAVIGATION:EXPAND_KPF_WIDGET').emit();
      this.setTreeType(TreeManagerTreeType.Kpf);
      return this.kpfTreeService.setActiveNodeById(node.id);
    }

    if (isShareLinkRootNode(node)) {
      this.setTreeType(TreeManagerTreeType.ShareLink);
      return this.shareLinkTreeService.setActiveNodeById(node.id);
    }

    if (isReleaseRootNode(node)) {
      this.setTreeType(TreeManagerTreeType.Release);
      return this.releaseTreeService.setActiveNodeById(node.id);
    }

    switch (this.currentTreeType) {
      case TreeManagerTreeType.Kpf:
        return this.kpfTreeService.setActiveNodeById(node.id);
      case TreeManagerTreeType.ShareLink:
        return this.shareLinkTreeService.setActiveNodeById(node.id);
      case TreeManagerTreeType.Release:
        return this.releaseTreeService.setActiveNodeById(node.id);
      case TreeManagerTreeType.Subsection:
        this.setTreeType(TreeManagerTreeType.Subsection);
        return EmitterService.get('SUBSECTION_NAVIGATION:SELECT_NODE').emit(node as SubsectionNode);
      case TreeManagerTreeType.ShareLinkPreview:
        return this.sharePreviewTreeService.navigateToNodeId(node.id);
      default:
        return EmitterService.get('SUBSECTION_NAVIGATION:SELECT_NODE').emit(node as SubsectionNode);
    }
  }

  navigateToPrevious(saveState: boolean) {
    switch (this.currentTreeType) {
      case TreeManagerTreeType.Kpf:
        return this.kpfTreeService.previous(saveState);
      case TreeManagerTreeType.ShareLink:
        return this.shareLinkTreeService.previous(saveState);
      case TreeManagerTreeType.Release:
        return this.releaseTreeService.previous(saveState);
      case TreeManagerTreeType.Subsection:
        return EmitterService.get('SUBSECTION_NAVIGATION:PREV_ITEM').emit();
      case TreeManagerTreeType.ShareLinkPreview:
        return this.sharePreviewTreeService.previous(saveState);
      default:
        return EmitterService.get('SUBSECTION_NAVIGATION:PREV_ITEM').emit();
    }
  }

  navigateToNext(saveState: boolean) {
    switch (this.currentTreeType) {
      case TreeManagerTreeType.Kpf:
        return this.kpfTreeService.next(saveState);
      case TreeManagerTreeType.ShareLink:
        return this.shareLinkTreeService.next(saveState);
      case TreeManagerTreeType.Release:
        return this.releaseTreeService.next(saveState);
      case TreeManagerTreeType.Subsection:
        //return this.subsectionTreeService.next(saveState);
        return EmitterService.get('SUBSECTION_NAVIGATION:NEXT_ITEM').emit();
      case TreeManagerTreeType.ShareLinkPreview:
        return this.sharePreviewTreeService.next(saveState);
      default:
        return EmitterService.get('SUBSECTION_NAVIGATION:NEXT_ITEM').emit();
    }
  }

  navigateToHome(saveState: boolean = true) {
    switch (this.currentTreeType) {
      case TreeManagerTreeType.Kpf:
        return this.kpfTreeService.navigateToHome(saveState);
      case TreeManagerTreeType.ShareLink:
        return this.shareLinkTreeService.navigateToHome(saveState);
      case TreeManagerTreeType.Release:
        return this.releaseTreeService.navigateToHome(saveState);
      case TreeManagerTreeType.Subsection:
        return EmitterService.get('SUBSECTION_NAVIGATION:HOME').emit();
      case TreeManagerTreeType.ShareLinkPreview:
        return this.sharePreviewTreeService.navigateToHome(saveState);
      default:
        return EmitterService.get('SUBSECTION_NAVIGATION:HOME').emit();
    }
  }

  getCurrentTreeState(): Observable<TreeState<BaseTreeNode>> {
    return this.currentTreeState$;
  }

  getCurrentState(): TreeState<BaseTreeNode> {
    switch (this.currentTreeType) {
      case TreeManagerTreeType.Kpf:
        return this.kpfTreeService.getCurrentState();
      case TreeManagerTreeType.ShareLink:
        return this.shareLinkTreeService.getCurrentState();
      case TreeManagerTreeType.Subsection:
        return this.subsectionTreeService.getCurrentState();
      case TreeManagerTreeType.ShareLinkPreview:
        return this.sharePreviewTreeService.getCurrentState();
      default:
        return this.subsectionTreeService.getCurrentState();
    }
  }
}
